import companyLogoPrimary from "../assets/jdSmallLogo.png";
import jdLogo from "../assets/JDLogo.png";

/** Logistics operator — mirrors backend `grnConfig.org` (GRN PDF defaults). */
export const LOGISTICS_ORG = {
  name: "Just Deliveries",
  addressLine1: "Address line 1",
  addressLine2: "",
  cityPin: "City, State - PIN",
  phone: "+91 XXXXX XXXXX",
};

export const PAGE_W = 841.89;
export const PAGE_H = 595.28;
export const MARGIN = 24;
export const CONTENT_W = PAGE_W - MARGIN * 2;
export const LEFT_COL_W = CONTENT_W * 0.58;
export const RIGHT_COL_W = CONTENT_W - LEFT_COL_W;
export const BORDER = 1;
export const STRONG_BORDER = 1.75;
export const LINE_COLOR = [0, 0, 0];
export const CLIENT_GRAY = [102, 102, 102];
export const PAD = 8;
export const X0 = MARGIN;

/**
 * Inventory-by-Product PDF page box — matches `WMS_backend/src/utils/grn/generateGrnPdf.js`:
 * A4 portrait (595.28 × 841.89 pt), content inset LEFT 50 / RIGHT 545.28.
 */
export const INVENTORY_PRODUCT_PAGE_W = 595.28;
export const INVENTORY_PRODUCT_PAGE_H = 841.89;
export const INVENTORY_PRODUCT_MARGIN = 50;
export const INVENTORY_PRODUCT_X0 = 50;
export const INVENTORY_PRODUCT_CONTENT_W = 495.28;
/** Header uses 56/44 and 61/39 splits; legacy export kept for compatibility. */
export const INVENTORY_PRODUCT_LEFT_COL_W = INVENTORY_PRODUCT_CONTENT_W * 0.58;
export const INVENTORY_PRODUCT_RIGHT_COL_W =
  INVENTORY_PRODUCT_CONTENT_W - INVENTORY_PRODUCT_LEFT_COL_W;

const INVENTORY_PRODUCT_COMPANY_LEFT_RATIO = 0.56;
const INVENTORY_PRODUCT_CLIENT_LEFT_RATIO = 0.61;
const INVENTORY_PRODUCT_HEADER_PAD = 8;

const PRIMARY_LOGO_MAX_W = 105;
const PRIMARY_LOGO_MAX_H = 38;
const SECONDARY_LOGO_MAX_W = 105;
const SECONDARY_LOGO_MAX_H = 32;
const LOGO_STACK_GAP = 5;

export function formatReportPeriod(start, end) {
  if (!start && !end) return "—";
  if (start && end) return `${start} to ${end}`;
  return start || end || "—";
}

export function formatGeneratedDateIst() {
  return new Date().toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatQtyCell(val) {
  if (val == null || val === "") return "";
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val ?? "");
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round((n + Number.EPSILON) * 1000) / 1000;
  return String(rounded);
}

export function sumNumeric(values) {
  return values.reduce((acc, val) => {
    const n = Number(val);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
}

export function buildColumnStyles(ratios, contentWidth = CONTENT_W) {
  const widths = ratios.map((r) => r * contentWidth);
  const drift = contentWidth - widths.reduce((sum, w) => sum + w, 0);
  widths[widths.length - 1] += drift;
  const styles = {};
  widths.forEach((w, i) => {
    styles[i] = {
      cellWidth: w,
      halign: i === 0 || i === 1 ? "left" : ratios[i] < 0.07 ? "center" : "right",
    };
    if (i === 1) styles[i].halign = "left";
    if (i === 2) styles[i].halign = "center";
  });
  return styles;
}

async function loadImageDataUrl(src) {
  const res = await fetch(src);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function measureImage(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
    img.onerror = () => resolve({ w: 1, h: 1 });
    img.src = dataUrl;
  });
}

export async function loadLedgerLogos() {
  const primaryLogoUrl = await loadImageDataUrl(companyLogoPrimary);
  const primaryLogoSize = await measureImage(primaryLogoUrl);
  const secondaryLogoUrl = await loadImageDataUrl(jdLogo);
  const secondaryLogoSize = await measureImage(secondaryLogoUrl);
  return {
    primary: { dataUrl: primaryLogoUrl, size: primaryLogoSize },
    secondary: { dataUrl: secondaryLogoUrl, size: secondaryLogoSize },
  };
}

function strokeRect(doc, x, y, w, h, lineWidth = BORDER) {
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(lineWidth);
  doc.rect(x, y, w, h);
}

function scaleLogoDrawSize(logoSize, maxW, maxH) {
  const scale = Math.min(maxW / logoSize.w, maxH / logoSize.h, 1);
  return { w: logoSize.w * scale, h: logoSize.h * scale };
}

function drawLogoStack(doc, x, y, columnW, blockH, primary, secondary) {
  const sizes = [];
  if (primary?.dataUrl && primary?.size) {
    sizes.push(scaleLogoDrawSize(primary.size, PRIMARY_LOGO_MAX_W, PRIMARY_LOGO_MAX_H));
  }
  if (secondary?.dataUrl && secondary?.size) {
    sizes.push(scaleLogoDrawSize(secondary.size, SECONDARY_LOGO_MAX_W, SECONDARY_LOGO_MAX_H));
  }
  if (!sizes.length) return;

  let drawY = y + PAD;

  const drawOne = (dataUrl, index) => {
    const drawW = sizes[index].w;
    const drawH = sizes[index].h;
    const drawX = x + PAD;
    try {
      doc.addImage(dataUrl, "PNG", drawX, drawY, drawW, drawH);
    } catch {
      // omit logo if image fails
    }
    drawY += drawH + LOGO_STACK_GAP;
  };

  if (primary?.dataUrl && primary?.size) drawOne(primary.dataUrl, 0);
  if (secondary?.dataUrl && secondary?.size) {
    drawOne(secondary.dataUrl, primary?.dataUrl && primary?.size ? 1 : 0);
  }
}

export function resolveClientFromContext(context) {
  const client = context.clientCompany || {};
  const address = client.address && typeof client.address === "object" ? client.address : {};
  const addressLines = [];
  if (address.line1) addressLines.push(String(address.line1).trim());
  if (address.line2) addressLines.push(String(address.line2).trim());
  const cityPin = [address.city, address.pincode].filter(Boolean).join(" ").trim();
  if (cityPin) addressLines.push(cityPin);
  if (address.state) addressLines.push(String(address.state).trim());
  if (!addressLines.length && typeof client.address === "string" && client.address.trim()) {
    addressLines.push(...client.address.split(",").map((s) => s.trim()).filter(Boolean));
  }

  return {
    name: client.name || client.companyName || "—",
    companyCode: client.companyCode || "—",
    email: client.email || "",
    phone: client.phone || "",
    addressLines,
  };
}

function resolveWarehouseFromContext(context) {
  const wh = context.warehouse || {};
  const city = wh.city?.cityName || wh.cityName || "";
  const state = wh.city?.stateRegion || wh.stateRegion || "";
  const locationParts = [wh.name, city, state].filter(Boolean);
  return {
    code: wh.code || "—",
    location: locationParts.length ? locationParts.join(", ") : wh.name || "—",
  };
}

function measureClientBlockHeight(doc, client) {
  const textW = LEFT_COL_W - PAD * 2;
  let h = PAD + 10 + 12 + 14;
  client.addressLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, textW);
    h += wrapped.length * 10;
  });
  if (client.phone) h += 12;
  if (client.email) h += 12;
  return h + PAD;
}

function metadataRowHeights(doc, w, rows) {
  const labelColW = w * 0.46;
  doc.setFont("times", "normal");
  doc.setFontSize(7.5);
  return rows.map((row) => {
    const valueLines = doc.splitTextToSize(String(row.value ?? "—"), w - labelColW - PAD * 2);
    const labelLines = doc.splitTextToSize(String(row.label), labelColW - PAD * 2);
    const lines = Math.max(valueLines.length, labelLines.length, 1);
    return Math.max(13, lines * 9 + 4);
  });
}

function drawMetadataGrid(doc, x, y, w, h, rows, rowHeights) {
  const labelColW = w * 0.46;
  strokeRect(doc, x, y, w, h);
  doc.setLineWidth(BORDER);
  doc.setDrawColor(...LINE_COLOR);
  doc.line(x + labelColW, y, x + labelColW, y + h);

  let rowY = y;
  rows.forEach((row, i) => {
    const rh = rowHeights[i];
    if (i > 0) {
      doc.line(x, rowY, x + w, rowY);
    }
    const textY = rowY + rh * 0.62;
    doc.setFont("times", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    doc.text(row.label, x + PAD, textY);
    doc.setFont("times", "normal");
    const valueLines = doc.splitTextToSize(String(row.value ?? "—"), w - labelColW - PAD * 2);
    doc.text(valueLines, x + labelColW + PAD, textY);
    rowY += rh;
  });
}

export function drawLedgerStatementHeader(
  doc,
  context,
  logos,
  { title, clientHeading = "Client Name & Address" } = {}
) {
  const splitX = X0 + LEFT_COL_W;
  let y = MARGIN;
  const headerTop = y;

  const titleH = 28;
  strokeRect(doc, X0, y, CONTENT_W, titleH, STRONG_BORDER);
  doc.setFont("times", "bold");
  doc.setFontSize(17);
  doc.setTextColor(0, 0, 0);
  doc.text(title, PAGE_W / 2, y + 19, { align: "center" });
  y += titleH;
  doc.setLineWidth(STRONG_BORDER);
  doc.setDrawColor(...LINE_COLOR);
  doc.line(X0, y, X0 + CONTENT_W, y);

  const companyH = 76;
  strokeRect(doc, X0, y, CONTENT_W, companyH, STRONG_BORDER);
  doc.setLineWidth(BORDER);
  doc.line(splitX, y, splitX, y + companyH);

  drawLogoStack(doc, X0, y, LEFT_COL_W, companyH, logos?.primary, logos?.secondary);

  const orgX = splitX + PAD;
  let oy = y + PAD + 10;
  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.text(LOGISTICS_ORG.name, orgX, oy);
  doc.setFont("times", "normal");
  doc.setFontSize(8);
  oy += 11;
  [LOGISTICS_ORG.addressLine1, LOGISTICS_ORG.addressLine2, LOGISTICS_ORG.cityPin, LOGISTICS_ORG.phone]
    .filter((line) => line != null && String(line).trim() !== "")
    .forEach((line) => {
      doc.text(String(line), orgX, oy);
      oy += 10;
    });
  y += companyH;

  const client = resolveClientFromContext(context);
  const warehouse = resolveWarehouseFromContext(context);
  const metaRows = [
    { label: "Customer Code", value: client.companyCode },
    { label: "Report Generated Date", value: formatGeneratedDateIst() },
    {
      label: "Report Period",
      value: formatReportPeriod(context.reportStartDate, context.reportEndDate),
    },
    { label: "Warehouse Location", value: warehouse.location },
    { label: "Warehouse Code", value: warehouse.code },
  ];

  doc.setFont("times", "normal");
  doc.setFontSize(8);
  const clientContentH = measureClientBlockHeight(doc, client);
  const metaRowHeights = metadataRowHeights(doc, RIGHT_COL_W, metaRows);
  const metaH = metaRowHeights.reduce((sum, rh) => sum + rh, 0);
  const clientBlockH = Math.max(clientContentH, metaH);

  strokeRect(doc, X0, y, CONTENT_W, clientBlockH, STRONG_BORDER);
  doc.setLineWidth(BORDER);
  doc.line(splitX, y, splitX, y + clientBlockH);
  drawMetadataGrid(doc, splitX, y, RIGHT_COL_W, clientBlockH, metaRows, metaRowHeights);

  doc.setFont("times", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text(clientHeading, X0 + PAD, y + PAD + 9);
  doc.setFontSize(9);
  doc.text(client.name, X0 + PAD, y + PAD + 22);

  doc.setFont("times", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...CLIENT_GRAY);
  let cy = y + PAD + 34;
  const clientTextW = LEFT_COL_W - PAD * 2;
  client.addressLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, clientTextW);
    doc.text(wrapped, X0 + PAD, cy);
    cy += wrapped.length * 10;
  });
  if (client.phone) {
    doc.text(`Contact: ${client.phone}`, X0 + PAD, cy + 2);
    cy += 11;
  }
  if (client.email) {
    doc.text(`Email: ${client.email}`, X0 + PAD, cy + 2);
  }

  y += clientBlockH;

  strokeRect(doc, X0, headerTop, CONTENT_W, y - headerTop, STRONG_BORDER);

  return y;
}

export function drawLedgerSignatureFooter(doc, context, startY) {
  const client = resolveClientFromContext(context);
  const footerH = 78;
  const pageH = doc.internal.pageSize.getHeight();
  let y = startY;

  if (y + footerH > pageH - MARGIN) {
    doc.addPage();
    y = MARGIN;
  }

  strokeRect(doc, X0, y, CONTENT_W, footerH, STRONG_BORDER);

  const labelsY = y + 42;
  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(`For ${LOGISTICS_ORG.name}`, X0 + PAD, labelsY);
  doc.text(`For ${client.name}`, X0 + CONTENT_W - PAD, labelsY, { align: "right" });

  const dividerY = labelsY + 8;
  doc.setLineWidth(BORDER);
  doc.setDrawColor(...LINE_COLOR);
  doc.line(X0, dividerY, X0 + CONTENT_W, dividerY);

  return y + footerH;
}

function measureInventoryProductClientBlockHeight(doc, client, clientLeftW) {
  const pad = INVENTORY_PRODUCT_HEADER_PAD;
  const textW = clientLeftW - pad * 2;
  let h = pad + 12 + 14;
  client.addressLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, textW);
    h += wrapped.length * 10;
  });
  if (client.phone) h += 12;
  if (client.email) h += 12;
  return h + pad;
}

function estimateInventoryProductLogoStackHeight(logos) {
  let total = INVENTORY_PRODUCT_HEADER_PAD;
  const add = (entry, maxW, maxH) => {
    if (!entry?.size) return;
    const scaled = scaleLogoDrawSize(entry.size, maxW, maxH);
    total += scaled.h + LOGO_STACK_GAP;
  };
  add(logos?.primary, PRIMARY_LOGO_MAX_W, PRIMARY_LOGO_MAX_H);
  add(logos?.secondary, SECONDARY_LOGO_MAX_W, SECONDARY_LOGO_MAX_H);
  return total > INVENTORY_PRODUCT_HEADER_PAD ? total : INVENTORY_PRODUCT_HEADER_PAD + 40;
}

/** Inventory-by-Product PDF only — stock-ledger style header (does not affect other report PDFs). */
export function drawInventoryByProductReportHeader(doc, context, logos) {
  const x0 = INVENTORY_PRODUCT_X0;
  const contentW = INVENTORY_PRODUCT_CONTENT_W;
  const pageW = INVENTORY_PRODUCT_PAGE_W;
  const margin = INVENTORY_PRODUCT_MARGIN;
  const pad = INVENTORY_PRODUCT_HEADER_PAD;
  const headerTop = margin;
  let y = headerTop;

  const companyLeftW = contentW * INVENTORY_PRODUCT_COMPANY_LEFT_RATIO;
  const companyRightW = contentW - companyLeftW;
  const companySplitX = x0 + companyLeftW;

  const clientLeftW = contentW * INVENTORY_PRODUCT_CLIENT_LEFT_RATIO;
  const clientRightW = contentW - clientLeftW;
  const clientSplitX = x0 + clientLeftW;

  // —— 1. Title row ——
  const titleH = 36;
  strokeRect(doc, x0, y, contentW, titleH, STRONG_BORDER);
  doc.setFont("times", "bold");
  doc.setFontSize(17);
  doc.setTextColor(0, 0, 0);
  doc.text("STOCK LEDGER REPORT", pageW / 2, y + titleH / 2 + 2, { align: "center" });
  y += titleH;

  // —— 2. Company row (56% logo | 44% org details) ——
  const orgLines = [
    LOGISTICS_ORG.addressLine1,
    LOGISTICS_ORG.addressLine2,
    LOGISTICS_ORG.cityPin,
    LOGISTICS_ORG.phone,
  ].filter((line) => line != null && String(line).trim() !== "");
  const orgTextH = pad + 12 + orgLines.length * 10.5 + pad;
  const companyH = Math.max(orgTextH, estimateInventoryProductLogoStackHeight(logos), 76);

  strokeRect(doc, x0, y, contentW, companyH, BORDER);
  doc.setLineWidth(BORDER);
  doc.setDrawColor(...LINE_COLOR);
  doc.line(companySplitX, y, companySplitX, y + companyH);

  drawLogoStackCentered(doc, x0, y, companyLeftW, companyH, logos?.primary, logos?.secondary);

  const orgX = companySplitX + pad;
  let oy = y + pad;
  doc.setFont("times", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text(LOGISTICS_ORG.name, orgX, oy);
  doc.setFont("times", "normal");
  doc.setFontSize(8);
  oy += 12;
  orgLines.forEach((line) => {
    doc.text(String(line), orgX, oy);
    oy += 10.5;
  });
  y += companyH;

  // —— 3. Client + metadata row (61% client | 39% table) ——
  const client = resolveClientFromContext(context);
  const warehouse = resolveWarehouseFromContext(context);
  const metaRows = [
    { label: "Customer Code", value: client.companyCode },
    { label: "Report Generated Date", value: formatGeneratedDateIst() },
    {
      label: "Report Period",
      value: formatReportPeriod(context.reportStartDate, context.reportEndDate),
    },
    { label: "Warehouse Location", value: warehouse.location },
    { label: "Warehouse Code", value: warehouse.code },
  ];

  doc.setFont("times", "normal");
  doc.setFontSize(8);
  const clientContentH = measureInventoryProductClientBlockHeight(doc, client, clientLeftW);
  const metaRowHeights = inventoryProductMetadataRowHeights(doc, clientRightW, metaRows);
  const metaH = metaRowHeights.reduce((sum, rh) => sum + rh, 0);
  const clientBlockH = Math.max(clientContentH, metaH);

  strokeRect(doc, x0, y, contentW, clientBlockH, BORDER);
  drawInventoryProductMetadataGrid(
    doc,
    clientSplitX,
    y,
    clientRightW,
    clientBlockH,
    metaRows,
    metaRowHeights
  );

  const clientTop = y + pad;
  doc.setFont("times", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text("Client Name & Address", x0 + pad, clientTop + 10);

  doc.setFont("times", "bold");
  doc.setFontSize(9);
  doc.text(client.name, x0 + pad, clientTop + 26);

  doc.setFont("times", "normal");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  let cy = clientTop + 38;
  const clientTextW = clientLeftW - pad * 2;
  client.addressLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, clientTextW);
    doc.text(wrapped, x0 + pad, cy);
    cy += wrapped.length * 10;
  });
  if (client.phone) {
    doc.text(client.phone, x0 + pad, cy + 2);
    cy += 11;
  }
  if (client.email) {
    doc.text(client.email, x0 + pad, cy + 2);
  }

  y += clientBlockH;

  strokeRect(doc, x0, headerTop, contentW, y - headerTop, STRONG_BORDER);

  return y + 6;
}

/** Inventory-by-Product PDF only — signature footer. */
export function drawInventoryByProductReportFooter(doc, context, startY) {
  const client = resolveClientFromContext(context);
  const footerH = 86;
  const x0 = INVENTORY_PRODUCT_X0;
  const contentW = INVENTORY_PRODUCT_CONTENT_W;
  const margin = INVENTORY_PRODUCT_MARGIN;
  const pageH = doc.internal.pageSize.getHeight();
  let y = startY + 10;

  if (y + footerH > pageH - margin) {
    doc.addPage();
    y = margin;
  }

  strokeRect(doc, x0, y, contentW, footerH, STRONG_BORDER);

  const labelsY = y + 40;
  doc.setFont("times", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text(`For ${LOGISTICS_ORG.name}`, x0 + PAD, labelsY);
  doc.text(`For ${client.name}`, x0 + contentW - PAD, labelsY, { align: "right" });

  const dividerY = labelsY + 10;
  doc.setLineWidth(BORDER);
  doc.setDrawColor(...LINE_COLOR);
  doc.line(x0 + PAD, dividerY, x0 + contentW - PAD, dividerY);

  return y + footerH;
}

function drawLogoStackCentered(doc, x, y, columnW, blockH, primary, secondary) {
  const sizes = [];
  if (primary?.dataUrl && primary?.size) {
    sizes.push({ url: primary.dataUrl, ...scaleLogoDrawSize(primary.size, PRIMARY_LOGO_MAX_W, PRIMARY_LOGO_MAX_H) });
  }
  if (secondary?.dataUrl && secondary?.size) {
    sizes.push({
      url: secondary.dataUrl,
      ...scaleLogoDrawSize(secondary.size, SECONDARY_LOGO_MAX_W, SECONDARY_LOGO_MAX_H),
    });
  }
  if (!sizes.length) return;

  const stackH =
    sizes.reduce((sum, s) => sum + s.h, 0) + Math.max(0, sizes.length - 1) * LOGO_STACK_GAP;
  let drawY = y + Math.max(PAD, (blockH - stackH) / 2);

  sizes.forEach((item) => {
    try {
      doc.addImage(item.url, "PNG", x + PAD, drawY, item.w, item.h);
    } catch {
      // omit logo if image fails
    }
    drawY += item.h + LOGO_STACK_GAP;
  });
}

function inventoryProductMetadataRowHeights(doc, w, rows) {
  const labelColW = w * 0.48;
  doc.setFont("times", "normal");
  doc.setFontSize(8);
  return rows.map((row) => {
    const valueLines = doc.splitTextToSize(
      String(row.value ?? "—"),
      w - labelColW - INVENTORY_PRODUCT_HEADER_PAD * 2
    );
    const labelLines = doc.splitTextToSize(
      String(row.label),
      labelColW - INVENTORY_PRODUCT_HEADER_PAD * 2
    );
    const lines = Math.max(valueLines.length, labelLines.length, 1);
    return Math.max(17, lines * 10 + 6);
  });
}

function drawInventoryProductMetadataGrid(doc, x, y, w, h, rows, rowHeights) {
  const labelColW = w * 0.48;
  const pad = INVENTORY_PRODUCT_HEADER_PAD;
  strokeRect(doc, x, y, w, h, BORDER);
  doc.setLineWidth(BORDER);
  doc.setDrawColor(...LINE_COLOR);
  doc.line(x + labelColW, y, x + labelColW, y + h);

  let rowY = y;
  rows.forEach((row, i) => {
    const rh = rowHeights[i];
    if (i > 0) {
      doc.line(x, rowY, x + w, rowY);
    }
    const textY = rowY + Math.min(rh - 4, 12);
    doc.setFont("times", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    const labelLines = doc.splitTextToSize(String(row.label), labelColW - pad * 2);
    doc.text(labelLines, x + pad, textY);
    doc.setFont("times", "normal");
    const valueLines = doc.splitTextToSize(String(row.value ?? "—"), w - labelColW - pad * 2);
    doc.text(valueLines, x + labelColW + pad, textY);
    rowY += rh;
  });
}

export const ledgerTableBaseStyles = {
  theme: "grid",
  styles: {
    font: "times",
    fontSize: 7,
    cellPadding: 2.5,
    lineWidth: BORDER,
    lineColor: LINE_COLOR,
    textColor: [0, 0, 0],
    overflow: "linebreak",
    valign: "middle",
  },
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    fontStyle: "bold",
    halign: "center",
    valign: "middle",
    minCellHeight: 22,
    fontSize: 7,
  },
};
